import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useUnitStore } from '../store/unitStore';
import { EyeIcon, EyeSlashIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';

// Optional login-identifier domain. Empty (default) means the raw identifier is
// sent as typed — no hardcoded @opmonitor.mil auto-append (see API contract §2).
const EMAIL_DOMAIN = (import.meta.env.VITE_EMAIL_DOMAIN as string | undefined) || '';

const BRIGADE_NAME = '86 Independent Signal Brigade';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, error } = useAuthStore();
  const { units, fetchUnits } = useUnitStore();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  // null = "Brigade HQ (all units)"; otherwise a specific unit id.
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  const selectableUnits = useMemo(() => units.filter(u => u.role !== 'HQ'), [units]);
  const selectedUnit = useMemo(
    () => (selectedUnitId ? units.find(u => u.id === selectedUnitId) ?? null : null),
    [selectedUnitId, units]
  );

  // Brand mark: selected unit's logo/color, else generic brigade mark. No per-code whitelist.
  const brandLogoUrl = selectedUnit?.logoUrl ?? null;
  const brandTitle = selectedUnit ? selectedUnit.name.toUpperCase() : BRIGADE_NAME.toUpperCase();
  const brandMonogram = (selectedUnit?.code ?? '86ISB').slice(0, 4);
  const brandColor = selectedUnit?.aorColor ?? '#3399ff';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    if (!identifier || !password) {
      setLocalError('Please enter your username and password');
      return;
    }

    // Configurable domain append — no hardcoded military domain.
    let loginId = identifier.trim();
    if (EMAIL_DOMAIN && !loginId.includes('@')) {
      loginId = `${loginId}@${EMAIL_DOMAIN}`;
    }

    // selectedUnitId is a view hint honored only for HQ principals; player units
    // are locked to their own unit by the backend regardless of this value.
    try {
      await login(loginId, password, selectedUnitId);
      navigate('/');
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || err.response?.data?.message || 'Login failed';
      setLocalError(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-command-bg grid-pattern flex items-center justify-center p-4">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-command-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl bg-command-panel border border-command-border mb-4 p-2">
            {brandLogoUrl ? (
              <img src={brandLogoUrl} alt={`${brandTitle} logo`} className="w-full h-full object-contain" />
            ) : (
              <div
                className="w-full h-full rounded-xl flex items-center justify-center text-sm font-bold text-white font-tactical"
                style={{ backgroundColor: brandColor }}
                aria-label={`${brandTitle} logo`}
              >
                {brandMonogram}
              </div>
            )}
          </div>
          <h1 className="text-3xl font-display font-bold text-command-accent tracking-wider">
            OpMonitor
          </h1>
          <p className="text-gray-500 font-tactical mt-2">{brandTitle}</p>
          <p className="text-gray-600 text-xs font-tactical mt-1">
            ST-2026 Summer Training — Command Center
          </p>
        </div>

        {/* Login Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-command-panel border border-command-border rounded-2xl p-8 shadow-lg"
        >
          <h2 className="text-xl font-semibold text-gray-100 mb-6 text-center">Secure Access</h2>

          {/* Error message */}
          {(error || localError) && (
            <div className="mb-6 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm text-center">
              {localError || error}
            </div>
          )}

          {/* Username field */}
          <div className="mb-4">
            <label className="block text-sm text-gray-400 mb-2 font-tactical">USERNAME</label>
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className={clsx(
                'w-full px-4 py-3 rounded-lg bg-command-bg border transition-colors',
                'text-gray-100 placeholder-gray-600',
                'focus:outline-none focus:border-command-accent focus:ring-1 focus:ring-command-accent',
                'border-command-border'
              )}
              placeholder="Enter your username"
              autoComplete="username"
            />
          </div>

          {/* Password field */}
          <div className="mb-4">
            <label className="block text-sm text-gray-400 mb-2 font-tactical">PASSWORD</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={clsx(
                  'w-full px-4 py-3 pr-12 rounded-lg bg-command-bg border transition-colors',
                  'text-gray-100 placeholder-gray-600',
                  'focus:outline-none focus:border-command-accent focus:ring-1 focus:ring-command-accent',
                  'border-command-border'
                )}
                placeholder="Enter your password"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              >
                {showPassword ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* View selection (HQ can pick a unit or all; player units are backend-locked) */}
          <div className="mb-6">
            <label className="block text-sm text-gray-400 mb-2 font-tactical">VIEW</label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowDropdown(!showDropdown)}
                className={clsx(
                  'w-full px-4 py-3 rounded-lg bg-command-bg border transition-colors',
                  'text-gray-100 flex items-center justify-between',
                  'focus:outline-none focus:border-command-accent focus:ring-1 focus:ring-command-accent',
                  'border-command-border'
                )}
              >
                <span>
                  {selectedUnit ? selectedUnit.name : 'Brigade HQ — All Units'}
                </span>
                <ChevronDownIcon
                  className={clsx(
                    'w-5 h-5 text-gray-400 transition-transform',
                    showDropdown && 'rotate-180'
                  )}
                />
              </button>

              {showDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-command-bg border border-command-border rounded-lg shadow-lg max-h-60 overflow-auto">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedUnitId(null);
                      setShowDropdown(false);
                    }}
                    className={clsx(
                      'w-full px-4 py-3 text-left text-gray-100 hover:bg-command-accent/20 transition-colors',
                      selectedUnitId === null && 'bg-command-accent/30'
                    )}
                  >
                    <div className="font-semibold">{BRIGADE_NAME}</div>
                    <div className="text-xs text-gray-400">HQ — View all units</div>
                  </button>
                  {selectableUnits.map((unit) => (
                    <button
                      key={unit.id}
                      type="button"
                      onClick={() => {
                        setSelectedUnitId(unit.id);
                        setShowDropdown(false);
                      }}
                      className={clsx(
                        'w-full px-4 py-3 text-left text-gray-100 hover:bg-command-accent/20 transition-colors border-t border-command-border flex items-center gap-2',
                        selectedUnitId === unit.id && 'bg-command-accent/30'
                      )}
                    >
                      <span
                        className="w-3 h-3 rounded-full border border-gray-600 flex-shrink-0"
                        style={{ backgroundColor: unit.aorColor }}
                      />
                      <div className="flex-1">
                        <div className="font-semibold">{unit.name}</div>
                        <div className="text-xs text-gray-400">{unit.code}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              HQ users can view all units. Player-unit accounts are locked to their own unit.
            </p>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={isLoading}
            className={clsx(
              'w-full py-3 rounded-lg font-bold transition-all duration-200',
              'bg-command-accent text-command-bg',
              'hover:shadow-glow hover:scale-[1.02]',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none',
              'font-display tracking-wider'
            )}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                AUTHENTICATING...
              </span>
            ) : (
              'ACCESS SYSTEM'
            )}
          </button>
        </form>

        {/* Security notice */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-600 font-tactical">
            🔒 SECURE CONNECTION • TLS 1.3 ENCRYPTED
          </p>
          <p className="text-xs text-gray-700 mt-2">
            Unauthorized access is prohibited and monitored.
          </p>
        </div>
      </div>
    </div>
  );
}
